/*
 * Vajihi Labs - Email Signature Builder
 * Outlook integration layer: every direct touchpoint with Office.js lives
 * here, so the UI/model/renderer code never has to know whether it's
 * actually running inside Outlook.
 */

/* global Office */

export function isOfficeAvailable(): boolean {
  return typeof Office !== "undefined" && !!Office.context;
}

export function isOutlookHost(): boolean {
  return isOfficeAvailable() && Office.context.host === Office.HostType.Outlook;
}

/** True when this pane is being viewed outside Outlook entirely (e.g. opened
 *  as a plain page for design work) — Outlook-only actions must be hidden or
 *  clearly disabled in this mode rather than silently failing. */
export function isStandalonePreview(): boolean {
  return !isOutlookHost();
}

export function isComposeApiSupported(): boolean {
  if (!isOfficeAvailable()) return false;
  try {
    return Office.context.requirements.isSetSupported("Mailbox", "1.10");
  } catch {
    return false;
  }
}

export interface OutlookProfile {
  displayName: string;
  emailAddress: string;
}

export function getUserProfile(): OutlookProfile | null {
  if (!isOutlookHost()) return null;
  const profile = Office.context.mailbox?.userProfile;
  if (!profile) return null;
  return { displayName: profile.displayName || "", emailAddress: profile.emailAddress || "" };
}

/** Returns the current compose item if one exists and looks like a real
 *  compose surface (has body.setSignatureAsync) — null otherwise, e.g. when
 *  opened from a read pane or outside Outlook. */
export function getComposeItem(): any | null {
  if (!isOutlookHost()) return null;
  const item = Office.context.mailbox?.item as any;
  if (!item || !item.body || typeof item.body.setSignatureAsync !== "function") return null;
  return item;
}

export interface InsertResult {
  ok: boolean;
  message: string;
}

/**
 * Disables Outlook's own default signature (so there's exactly one signature
 * source, not two stacked) and then writes the given HTML into the compose
 * item via setSignatureAsync. Never throws — always resolves with a result
 * the UI can show directly.
 */
export function insertSignature(html: string): Promise<InsertResult> {
  if (!isComposeApiSupported()) {
    return Promise.resolve({
      ok: false,
      message:
        "Your Outlook version doesn't support inserting signatures automatically here. Copy the preview above and paste it into Outlook's own signature settings instead.",
    });
  }

  const item = getComposeItem();
  if (!item) {
    return Promise.resolve({
      ok: false,
      message: "Open this from a new email (compose window) to insert a signature.",
    });
  }

  return new Promise((resolve) => {
    const applySignature = () => {
      item.body.setSignatureAsync(html, { coercionType: Office.CoercionType.Html }, (result: Office.AsyncResult<void>) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          resolve({ ok: true, message: "Signature inserted — check your draft." });
        } else {
          resolve({ ok: false, message: `Couldn't insert the signature: ${result.error?.message ?? "unknown error"}` });
        }
      });
    };

    if (typeof item.disableClientSignatureAsync === "function") {
      item.disableClientSignatureAsync(() => applySignature());
    } else {
      applySignature();
    }
  });
}
